<template>
  <wiskModal :visible="!!editAction" @hide="setGlobalAction({ type: 'posModifierEdit', action: null })" class="pos-modifier-editor" :okText="translations.txtGenericSave" @ok="saveChanges"
    preventOKClose size="md" headerClass="px-3" :okDisabled="loading">

    <template v-slot:modal-header>
      <b-row v-if="modifier?.id">
        <b-col cols="12">
          <h5 class="modal-title mb-2">
            {{ modifier?.title }} ({{ translations.groupPOSModifierTypes[modifier?.mapping?.type] }})
          </h5>
        </b-col>
        <b-col cols="12" class="d-flex justify-content-between">
          <b-form-radio-group size="sm" style="margin-left: 5px;" buttons v-model="selectedEditorTab" button-variant="outline-primary">
            <b-form-radio :value="0"> {{ translations.txtPOSModifierEdit }} </b-form-radio>
            <b-form-radio :value="1"> {{ translations.txtPOSModifierUsedWith }} </b-form-radio>
          </b-form-radio-group>
          <b-button :variant="modifier.archived ? 'success' : 'danger'" size="sm" @click="save({ type: 'archive', value: !modifier.archived })">
            {{ modifier.archived ? translations.txtGenericRestore : translations.txtGenericArchive }}
          </b-button>
        </b-col>
      </b-row>
    </template>

  <wiskInputGroup v-if="modifier && selectedEditorTab === 0" style="min-height: 450px;">
    <template v-if="editAction?.id">
      <wiskInput v-model="modifier.title" :label="translations.txtGenericTitle" readonly />
      <wiskInput v-model="modifier.plu_number" label="PLU Number" readonly />
    </template>

    <b-row>
      <b-col>
        <wiskSelect v-model="mappingType" :items="posModifierMappingTypes" :label="translations.txtPOSModifierMappingType" @change="onTypeChange" required class="mb-0" />
      </b-col>
    </b-row>
    <b-row v-for="(command, index) in commands" :key="index">
      <hr class="my-3" />
      <b-col :cols="command.type === 'multiplier' ? 6 : 12">
        <wiskSelect v-model="command.type" :items="subTypes" :label="translations.txtPOSModifierMappingSubtype" @change="command.value = null;" required class="mb-1" />
      </b-col>
      <b-col :cols="command.type === 'multiplier' ? 6 : 12">
        <template v-if="mappingType === 'ingredient_change'">
          <addIngredient v-if="!command.value?.ingredient" vertical @add="command.value = $event.value; calculateCost(command)" :hideServingSize="command.type === 'remove'"
            :btAddLabel="translations.txtPOSModifierApplyChange" compact />
          <itemViewMini v-else :item="getIngredientItem(command.value.ingredient.value)">
            <div class="w-100 pt-4 text-center">
              <template v-if="command.type !== 'remove'">
                {{ getMeasurementDisplayString(command.value.serving_size.measurement) }}
                <br/>
                {{ translations.txtGenericCost }}:
                <wiskLoading loading v-if="loadingDetails" class="d-inline-block ms-1 position-relative" />
                <template v-else>
                  {{ currencyFormat(getCost(command.value.ingredient)) }}
                </template>
                <v-menu v-if="details.ingredientCostsByTypeById && !getCost(command.value.ingredient)"
                  :triggers="['hover']" class="d-inline-block ms-1">
                  <icon name="wisk-information-button" :scale=".7" class="text-primary" />
                  <template #popper>
                    <div class="p-1">
                      {{ translations.txtPOSModifierCostCannotBeCalculated }}
                    </div>
                  </template>
                </v-menu>
              </template>
              <template v-else>
                {{ translations.txtPOSModifierCostCannotBeCalculated }}
              </template>
            </div>
            <b-button variant="link" @click="command.value = null;" class="position-absolute" style="top: 0; right: 0;" v-tooltip="translations.txtGenericRemove">
              <icon name="wisk-trash" class="text-danger" :scale="0.9" />
            </b-button>
          </itemViewMini>
          <b-button v-if="index" variant="link" size="md" title="Remove" @click="modifier?.mapping?.value.splice(index, 1)" class="mt-2">
            <icon class="text-danger" name="wisk-minus" :scale="1" />
          </b-button>
          <b-button v-if="index === (modifier?.mapping?.value?.length || 1) - 1" variant="link" size="md" title="Add" @click="addModifierValue" class="mt-2">
            <icon class="" name="wisk-plus" :scale="1" />
          </b-button>
        </template>

        <template v-if="command.type === 'base_change'">
          <addIngredient v-if="!command.value?.serving_size" vertical @add="command.value = $event.value;" hideIngredient
            :btAddLabel="translations.txtPOSModifierApplyChange" compact />
          <b-alert v-else variant="secondary" show class="text-center mt-2">
            {{ getMeasurementDisplayString(command.value.serving_size.measurement) }}
            <b-button variant="link" @click="command.value = null" class="position-absolute" style="top: 0; right: 0;" v-tooltip="translations.txtGenericRemove">
              <icon name="wisk-trash" class="text-danger" :scale="0.9" />
            </b-button>
          </b-alert>
        </template>

        <b-row v-if="command.type === 'multiplier'">
          <b-col>
            <wiskInput v-model="command.value" :label="translations.txtPOSModifierMultiplier" inputType="number" :decimals="2" decimalsAsNeeded autofocus triggerINputOnSet/>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-button v-if="mappingType === 'ingredient_change' && !(modifier?.mapping?.value?.length || 0)" variant="link" size="md" title="Add" @click="addModifierValue" class="mt-2">
      <icon class="" name="wisk-plus" :scale="1" />
    </b-button>
  </wiskInputGroup>
  <div v-if="selectedEditorTab === 1" style="min-height: 450px;">
    <wiskInput infoTooltipKey="a2dec3ca-008a-4c2c-82ad-50f76e06c8d2" :label="translations.txtGenericSince" inputType="datepicker" :max="new Date()" v-model="since" @input="getDetails" />
    <itemUsageGrid :item="{ used_by: { pos_items: details?.used_with?.pos_item_ids || [] } }" />
  </div>

  <wiskLoading :loading="loading" />
  </wiskModal>

</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import isEqual from 'lodash.isequal'
import merge from 'lodash.merge'
import { DateTime } from 'luxon'
import api from '@/api'
import { /*getPOSItemIngredientDescription, */getMeasurementDisplayString, currencyFormat } from '@/modules/utils'
import addIngredient from '@/components/common/IngredientAdd'
import itemViewMini from '@/components/bottles/ItemViewMini'
import itemUsageGrid from '@/components/bottles/ItemUsageGrid'

export default {
  name: 'POSModifierEdit',
  components: { addIngredient, itemViewMini, itemUsageGrid },
  props: {
    editAction: Object
  },
  data() {
    return {
      modifier: null,
      mappingType: null,
      subTypes: [],
      selectedEditorTab: 0,
      details: {},
      loading: false,
      since: DateTime.fromJSDate(new Date()).minus({ days: 30 }).toJSDate()
    }
  },
  computed: {
    ...mapState(['translations', 'posModifiersById', 'bottlesById', 'subrecipesById']),
    ...mapGetters(['posModifierMappingTypes']),
    commands() {
      if (this.modifier) {
        if (this.mappingType === 'not_mapped' || this.mappingType === 'label') {
          return []
        }
        return this.mappingType === 'ingredient_change' ? this.modifier?.mapping?.value : this.modifier?.mapping?.value ? [this.modifier?.mapping?.value] : []
      }
      return []
    }
  },
  methods: {
    ...mapActions(['setGlobalAction', 'notify']),
    getMeasurementDisplayString,
    currencyFormat,
    getIngredientItem(ingredient) {
      let id = ingredient?.item_id || ingredient?.subrecipe_id,
        found = ingredient?.item_id ? this.bottlesById[id] : this.subrecipesById[id]

      return found ? { ...found, titleSuffix: found?.titleSuffix || '' } : null
    },
    onTypeChange({ id, subTypes }) {
      let modifier = merge({}, this.modifier)
      this.subTypes = subTypes ? subTypes : []
      if (id === 'ingredient_change') {
        modifier.mapping.value = []
      } else if (id === 'measurement_change') {
        modifier.mapping.value = {
          type: 'multiplier',
          value: 1
        }
      } else {
        modifier.mapping.value = {}
      }
      this.modifier = { ...modifier }
    },
    saveChanges() {
      const payload = {
        ...this.modifier?.mapping,
        type: this.mappingType,
      }
      this.save({ type: 'mapping', value: payload })
    },
    save(operations) {
      this.loading = true
      if (this.modifier?.id) {
        api.updatePOSModifier(this.modifier?.id, operations)
        .then(() => {
          this.loading = false
          if (this.editAction?.onChange) {
            this.editAction.onChange()
          }
          this.setGlobalAction({ type: 'posModifierEdit', action: null })
        })
        .catch(() => {
          this.loading = false
        })
      } else if (this.editAction?.ids) {
        api.updatePOSModifierBatch({ pos_modifier_ids: this.editAction.ids, operation: operations })
        .then(() => {
          this.notify({
            message: this.translations.txtGenericOperationSuccess,
            type: 'success'
          })
          if (this.editAction?.onChange) {
            this.editAction.onChange()
          }
          this.loading = false
          this.setGlobalAction({ type: 'posModifierEdit', action: null })
        })
      }
    },
    getCost(ingredient) {
      const costByTypeById = this.details.ingredientCostsByTypeById?.[ingredient.type]?.[ingredient.value.item_id || ingredient.value.subrecipe_id]
      return costByTypeById?.cost
    },
    async calculateCost(command) {
      if (command.value?.ingredient) {
        const result = await api.POSModifierCalculateCost({
          type: this.mappingType,
          value: [command]
        }),
          ingredient = command.value.ingredient

        if (!this.details.ingredientCostsByTypeById) {
          this.details.ingredientCostsByTypeById = {}
        }
        if (!this.details.ingredientCostsByTypeById[ingredient.type]) {
          this.details.ingredientCostsByTypeById[ingredient.type] = {}
        }

        this.details.ingredientCostsByTypeById[ingredient.type][ingredient.value.item_id || ingredient.value.subrecipe_id] = result[0]
      }
    },
    getDetails(since) {
      this.loadingDetails = true
      if (since) {
        since = DateTime.fromJSDate(since).toISO()
      }
      this.modifier = merge({}, this.posModifiersById[this.editAction.id])
      this.mappingType = this.modifier?.mapping?.type
      this.subTypes = this.posModifierMappingTypes.find(({ id }) => id === this.mappingType)?.subTypes || []
      api.getPOSModifierDetails(this.modifier.id, since).then(data => {
        let ingredientCostsByItemId = {},
          ingredientCostsBySubrecipeId = {}

        data.ingredient_costs.forEach(ingredient => {
          if (ingredient.ingredient.type === 'item') {
            ingredientCostsByItemId[ingredient.ingredient.value.item_id] = ingredient
          } else {
            ingredientCostsBySubrecipeId[ingredient.ingredient.value.subrecipe_id] = ingredient
          }
        })

        this.details = {
          ...data,
          ingredientCostsByTypeById: {
            item: ingredientCostsByItemId,
            subrecipe: ingredientCostsBySubrecipeId
          }
        }
        this.loadingDetails = false
      })
      .finally(() => {
        this.loadingDetails = false
      })
    },
    addModifierValue() {
      if (!this.modifier.mapping.value) {
        this.modifier.mapping.value = []
      }
      this.modifier.mapping.value.push({ type: '', value: {} })
    }
  },
  created() {},
  watch: {
    editAction: {
      immediate: true,
      deep: true,
      handler(editAction) {
        if (editAction?.id) {
          this.getDetails()
        } else {
          this.modifier = { id: 0, title: this.translations.txtPOSModifierNew, mapping: { type: 'not_mapped' } }
        }
      }
    },
    posModifiersById() {
      if (this.modifier?.id && !isEqual(this.modifier, this.posModifiersById[this.modifier.id])) {
        this.modifier = merge({}, this.posModifiersById[this.modifier.id])
      }
    }
  }
}
</script>

<style lang="scss">
</style>
